import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  Bookmark,
  BookmarkCheck,
  BriefcaseBusiness,
  CalendarClock,
  CircleHelp,
  ExternalLink,
  MapPin,
  MessageCircle,
  ShieldCheck,
  WalletCards,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Button } from './components/ui/button';
import JobApplyDialog from './JobApplyDialog';
import { STATIC_JOBS, STATIC_JOBS_VERIFIED_AT } from './data/staticJobs';
import { useJobInteractions } from './jobInteractions';

const formatPay = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(amount);

const buildConciergePrompt = (job) => (
  `Find me approved ${job.specialty} jobs in ${job.state} paying at least ${formatPay(job.weeklyGross)} gross per week.`
);

const Fact = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#E2E0DA] bg-white p-4">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#88827C]">
      <Icon className="h-4 w-4" strokeWidth={1.8} /> {label}
    </div>
    <div className="mt-2 text-base font-medium leading-6 text-[#34302D]">{value}</div>
  </div>
);

export default function JobDetail() {
  const { jobId } = useParams();
  const job = useMemo(() => STATIC_JOBS.find((item) => item.id === jobId), [jobId]);
  const [applyOpen, setApplyOpen] = useState(false);
  const {
    savedJobIds,
    startedApplicationIds,
    toggleSavedJob,
    markApplicationStarted,
  } = useJobInteractions();

  useEffect(() => {
    const priorTitle = document.title;
    document.title = job ? `${job.title} | HeyTraveler` : 'Job not found | HeyTraveler';
    return () => { document.title = priorTitle; };
  }, [job]);

  if (!job) {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center bg-[#F8F7F3] px-4 text-[#2C2825]">
        <div className="max-w-lg rounded-2xl border border-[#E2E0DA] bg-white p-8 text-center shadow-[0_16px_50px_rgba(44,40,37,0.08)]">
          <CircleHelp className="mx-auto h-8 w-8 text-[#77716C]" strokeWidth={1.6} />
          <h1 className="mt-4 font-serif text-3xl" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>That listing is not in this snapshot.</h1>
          <p className="mt-3 leading-7 text-[#69645F]">It may have closed or the link may be incomplete. Browse the current Care Team snapshot instead.</p>
          <Button asChild className="mt-6 h-11 rounded-xl bg-[#2C2825] px-5 text-white hover:bg-[#1F1C1A]">
            <Link to="/jobs">Back to jobs</Link>
          </Button>
        </div>
      </main>
    );
  }

  const isSaved = savedJobIds.has(job.id);
  const applicationStarted = startedApplicationIds.has(job.id);
  const conciergeUrl = `/concierge?message=${encodeURIComponent(buildConciergePrompt(job))}`;
  const similarJobs = STATIC_JOBS.filter(
    (item) => item.id !== job.id && (item.specialty === job.specialty || item.profession === job.profession),
  ).slice(0, 3);

  return (
    <main className="min-h-screen min-h-[100dvh] bg-[#F8F7F3] text-[#2C2825]">
      <header className="border-b border-[#E5E3DD] bg-[#F8F7F3]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="font-serif text-2xl lowercase tracking-[-0.02em] text-[#2C2825]" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>
            hey, traveler.
          </Link>
          <Button asChild variant="outline" className="h-10 rounded-xl border-[#D8D6CF] bg-white px-4 text-[#3B3835] shadow-none hover:bg-[#F0EFEA]">
            <Link to="/concierge"><MessageCircle className="h-4 w-4" /> Talk to Alex</Link>
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-7 sm:px-6 sm:py-10 lg:px-8">
        <Link to="/jobs" className="inline-flex min-h-11 items-center gap-2 rounded-lg pr-3 text-sm font-medium text-[#5F5A56] hover:text-[#2C2825] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D8A79]/40" data-testid="back-to-jobs">
          <ArrowLeft className="h-4 w-4" /> Back to jobs
        </Link>

        <div className="mt-5 grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          <section>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#E9F3EC] px-3 py-1.5 text-xs font-semibold text-[#2F6B52]">
              <ShieldCheck className="h-4 w-4" strokeWidth={2} /> Care Team source checked
            </div>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.05] tracking-[-0.025em] sm:text-5xl" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }} data-testid="job-detail-title">
              {job.title}
            </h1>
            <p className="mt-4 text-lg text-[#625E5A]">{job.city}, {job.state} · {job.employmentType}</p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <Fact icon={WalletCards} label="Weekly pay" value={`${formatPay(job.weeklyGross)} gross`} />
              <Fact icon={MapPin} label="Location" value={`${job.city}, ${job.state}`} />
              <Fact icon={BriefcaseBusiness} label="Specialty" value={`${job.profession} · ${job.specialty}`} />
              <Fact icon={CalendarClock} label="Shift" value={job.shift || 'Not verified in this snapshot'} />
            </div>

            <div className="mt-8 rounded-2xl border border-[#E2E0DA] bg-white p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-[#34302D]">Before you apply</h2>
              <p className="mt-2 leading-7 text-[#69645F]">
                Confirm the facility, start date, duration, complete schedule, and experience or license requirements on Care Team&apos;s application page. Those details are not verified in this testing snapshot.
              </p>
              <div className="mt-5 flex items-start gap-3 rounded-xl bg-[#F4F1EB] p-4 text-sm leading-6 text-[#625E5A]">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#2F6B52]" strokeWidth={1.8} />
                <p>Source-checked {STATIC_JOBS_VERIFIED_AT}. Availability and pay can change. Starting an application is not the same as submitting one.</p>
              </div>
              <a href={job.sourceUrl} target="_blank" rel="noreferrer" className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg text-sm font-semibold text-[#3E5F4E] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6D8A79]/40" data-testid="care-team-source-link">
                View the Care Team source <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            {similarJobs.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-3xl" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>Similar Care Team jobs</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {similarJobs.map((item) => (
                    <Link key={item.id} to={`/jobs/${item.id}`} className="rounded-2xl border border-[#E2E0DA] bg-white p-4 transition-[border-color,box-shadow] duration-200 hover:border-[#C8C4BC] hover:shadow-[0_12px_30px_rgba(44,40,37,0.07)]">
                      <div className="font-semibold leading-snug text-[#34302D]">{item.title}</div>
                      <div className="mt-2 text-sm text-[#716B66]">{item.city}, {item.state} · {formatPay(item.weeklyGross)}/week</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>

          <aside className="rounded-2xl border border-[#DCD9D1] bg-white p-5 shadow-[0_18px_50px_rgba(44,40,37,0.08)] lg:sticky lg:top-6" data-testid="application-card">
            {applicationStarted && (
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#EEF5F0] px-3 py-1.5 text-xs font-semibold text-[#2F6B52]">
                <ArrowUpRight className="h-4 w-4" /> Application started
              </div>
            )}
            <div className="text-sm text-[#716B66]">Care Team lists</div>
            <div className="mt-1 text-3xl font-semibold tabular-nums text-[#2C2825]">{formatPay(job.weeklyGross)}</div>
            <div className="mt-1 text-sm text-[#716B66]">gross per week</div>

            <Button onClick={() => setApplyOpen(true)} className="mt-6 h-12 w-full rounded-xl bg-[#2C2825] text-white shadow-none hover:bg-[#1F1C1A]" data-testid="open-apply-dialog">
              {applicationStarted ? 'Continue application' : 'Apply with Care Team'} <ArrowUpRight className="h-4 w-4" />
            </Button>
            <Button onClick={() => toggleSavedJob(job.id)} variant="outline" className="mt-2 h-12 w-full rounded-xl border-[#D8D6CF] bg-white text-[#3B3835] shadow-none hover:bg-[#F5F3EE]" data-testid="save-job-detail" aria-pressed={isSaved}>
              {isSaved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
              {isSaved ? 'Saved' : 'Save for later'}
            </Button>

            <p className="mt-5 text-xs leading-5 text-[#7A746F]">
              Application happens on Care Team Solutions&apos; Staffing Referrals page. HeyTraveler does not submit or share your information.
            </p>

            <div className="mt-5 border-t border-[#ECE9E3] pt-5">
              <Button asChild variant="ghost" className="h-auto w-full justify-start whitespace-normal px-0 py-2 text-left text-sm font-medium text-[#3E5F4E] hover:bg-transparent hover:text-[#294A39]">
                <Link to={conciergeUrl}>Ask Alex to find similar approved jobs <ArrowUpRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </aside>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-[#E0DED8] pt-6 text-xs leading-relaxed text-[#77736F] sm:flex-row sm:items-center sm:justify-between">
          <p>HeyTraveler currently searches only approved Care Team Solutions opportunities supplied through Staffing Referrals.</p>
          <Link to="/privacy" className="shrink-0 underline underline-offset-4 hover:text-[#3B3835]">Privacy policy</Link>
        </div>
      </div>

      <JobApplyDialog
        job={job}
        open={applyOpen}
        onOpenChange={setApplyOpen}
        onApplicationStarted={markApplicationStarted}
      />
    </main>
  );
}
